// Firebase configuration for GoTurkeyTransfer
// This file initializes Firebase for push notifications

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration - will be loaded from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase app
let app = null;
let messaging = null;

export const initializeFirebase = async () => {
  try {
    // Check if Firebase is configured
    if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
      console.log('Firebase not configured - push notifications disabled');
      return { app: null, messaging: null };
    }
    
    // Initialize app if not already done
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApp();
    }
    
    // Check if messaging is supported
    const supported = await isSupported();
    if (!supported) {
      console.log('Firebase messaging not supported in this browser');
      return { app, messaging: null };
    }
    
    messaging = getMessaging(app);
    console.log('Firebase initialized successfully');
    
    return { app, messaging };
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    return { app: null, messaging: null };
  }
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return { success: false, error: 'Notifications not supported' };
    }
    
    // Check if Firebase is properly configured before requesting permission
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !vapidKey) {
      // Silently return - Firebase not configured
      return { success: false, error: 'Firebase not configured', notConfigured: true };
    }
    
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { success: false, error: 'Permission denied' };
    }
    
    // Initialize Firebase if not done
    const { messaging: msg } = await initializeFirebase();
    
    if (!msg) {
      return { success: false, error: 'Firebase messaging not available' };
    }
    
    // Get FCM token
    const token = await getToken(msg, { 
      vapidKey,
      serviceWorkerRegistration: await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
    });
    
    if (!token) {
      return { success: false, error: 'Failed to get FCM token' };
    }
    
    console.log('FCM Token obtained:', token.substring(0, 20) + '...');
    
    return { success: true, token };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, error: error.message };
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.log('Messaging not initialized');
    return null;
  }
  
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    
    // Play notification sound
    playNotificationSound();
    
    // Call the callback
    if (callback) {
      callback(payload);
    }
  });
};

// Play notification sound
export const playNotificationSound = () => {
  try {
    const audio = new Audio('/order_sound.mp3');
    audio.volume = 1.0;
    audio.play().catch(err => {
      console.log('Could not play notification sound:', err);
    });
  } catch (error) {
    console.log('Error playing notification sound:', error);
  }
};

// Register service worker for background notifications
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('Service Worker registered:', registration.scope);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

export { app, messaging };
